import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CoachPanel = ({ isOpen, onClose, initialQuery, userProfile, aiLevel }) => {
  const [query, setQuery] = useState("");
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (initialQuery && isOpen) setQuery(initialQuery);
  }, [initialQuery, isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || loading) return;
    const userQuery = query.trim();
    setQuery("");
    setError(null);
    setConversation(prev => [...prev, { role: "user", content: userQuery }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/coach`, {
        query: userQuery,
        context: { user_profile: userProfile, ai_level: aiLevel, intent_type: "learn" }
      });
      const answer = response.data.answer;
      setConversation(prev => [...prev, {
        role: "assistant",
        content: answer.text,
        explanation: answer.explanation,
        actions: answer.actions,
        sources: answer.sources,
        followUps: response.data.follow_up_prompts || []
      }]);
    } catch {
      setError("Failed to get response. Please try again.");
      setConversation(prev => [...prev, {
        role: "assistant",
        content: "I apologize, but I encountered an issue. Please try again.",
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = (prompt) => {
    setQuery(prompt);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="coach-panel">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg h-full flex flex-col"
        style={{
          background: "rgba(10, 25, 47, 0.85)",
          backdropFilter: "blur(24px)",
          borderLeft: "1px solid rgba(255, 255, 255, 0.08)",
          animation: "slide-in-right 0.3s ease-out"
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#14B8A6] flex items-center justify-center coach-glow">
              <Sparkles className="w-5 h-5 text-[#050B14]" />
            </div>
            <div>
              <h2 className="font-headline font-medium text-[#F8FAFC]">AI Coach</h2>
              <p className="text-xs text-[#94A3B8]">Your personalized AI guide</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/[0.06] transition-colors" data-testid="coach-close">
            <X className="w-5 h-5 text-[#94A3B8]" />
          </button>
        </div>

        {/* Context */}
        <div className="px-4 py-2 border-b border-white/[0.04] flex items-center gap-2 text-xs">
          <span className="text-[#94A3B8]">Context:</span>
          <span className="px-2 py-0.5 rounded-full bg-[#6D28D9]/10 text-[#A78BFA] capitalize">{userProfile}</span>
          <span className="px-2 py-0.5 rounded-full bg-[#14B8A6]/10 text-[#14B8A6] capitalize">{aiLevel}</span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {conversation.length === 0 && (
            <div className="text-center py-12" data-testid="coach-empty-state">
              <div className="w-16 h-16 rounded-full bg-[#14B8A6]/10 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-[#14B8A6]" />
              </div>
              <h3 className="font-headline font-medium text-[#F8FAFC] mb-2">How can I help you?</h3>
              <p className="text-sm text-[#94A3B8] max-w-xs mx-auto">
                Ask me anything about AI transformation, governance, learning pathways, or best practices.
              </p>
            </div>
          )}

          {conversation.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`coach-message-${idx}`}>
              <div className={`max-w-[85%] ${
                msg.role === "user" 
                  ? "bg-[#14B8A6] text-[#050B14] rounded-2xl rounded-tr-sm px-4 py-3" 
                  : "bg-white/[0.04] border border-white/[0.06] rounded-2xl rounded-tl-sm px-4 py-3"
              }`}>
                <p className={`text-sm ${msg.isError ? 'text-red-400' : ''}`}>{msg.content}</p>

                {msg.explanation && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-xs text-[#94A3B8] mb-1">Explanation:</p>
                    <p className="text-sm text-[#F8FAFC]">{msg.explanation}</p>
                  </div>
                )}

                {msg.actions && msg.actions.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-[#94A3B8] mb-2">Suggested Actions:</p>
                    <ul className="space-y-1">
                      {msg.actions.map((action, actionIdx) => (
                        <li key={actionIdx} className="flex items-start gap-2 text-sm">
                          <svg className="w-4 h-4 text-[#14B8A6] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                          </svg>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {msg.sources && msg.sources.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {msg.sources.map((source, srcIdx) => (
                      <span key={srcIdx} className="source-badge text-xs px-2 py-1 rounded-full">{source.title}</span>
                    ))}
                  </div>
                )}

                {msg.followUps && msg.followUps.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-xs text-[#94A3B8] mb-2">Related questions:</p>
                    <div className="space-y-1">
                      {msg.followUps.map((followUp, fuIdx) => (
                        <button
                          key={fuIdx}
                          onClick={() => handleFollowUp(followUp)}
                          className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-[#F8FAFC]"
                        >
                          {followUp}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#14B8A6] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-[#14B8A6] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-[#14B8A6] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {error && <div className="px-4 py-2 bg-red-500/10 text-red-400 text-sm">{error}</div>}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-white/[0.06]">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask me anything about AI..."
              className="flex-1 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[#F8FAFC] placeholder-[#94A3B8]/60 focus:ring-1 focus:ring-[#14B8A6]/50 focus:border-[#14B8A6]/30 focus:outline-none text-sm"
              disabled={loading}
              data-testid="coach-input"
            />
            <button
              type="submit"
              disabled={!query.trim() || loading}
              className="px-4 py-3 rounded-xl bg-[#14B8A6] text-[#050B14] font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0D9488] transition-colors"
              data-testid="coach-submit"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
